import 'react-perfect-scrollbar/dist/css/styles.css';
import '@mui/lab';

import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  split
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { useAuth0 } from '@auth0/auth0-react';
import { StyledEngineProvider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs as DateAdapter } from '@mui/x-date-pickers/AdapterDayjs';
import { Toaster } from 'react-hot-toast';
import { useRecoilValue } from 'recoil';
import { currentUser } from 'recoil/User';

import LoadingScreen from './components/LoadingScreen';
import { SettingsProvider } from './contexts/SettingsContext';
import Router from './routes';
import ThemeConfig from './theme';
import GlobalStyles from './theme/globalStyles';
import ThemePrimaryColor from './theme/ThemePrimaryColor';

const useStyles = makeStyles(() => ({
  backdrop: {
    color: '#fff'
  },
  containerToast: {
    margin: 0,
    padding: 0
  }
}));

const App = () => {
  const { isLoading } = useAuth0();
  const user = useRecoilValue(currentUser);
  const tokenLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${user?.token}` || ''
      }
    };
  });

  const wsLink = new WebSocketLink({
    uri: `wss://${process.env.REACT_APP_HASURA_URL}`,
    options: {
      lazy: true,
      reconnect: true,
      connectionParams: {
        headers: {
          'content-type': 'application/json',
          'Hasura-Client-Name': 'rayo-ava',
          authorization: user ? `Bearer ${user?.token}` : ''
        }
      }
    }
  });

  const httpLink = new HttpLink({
    uri: `https://${process.env.REACT_APP_HASURA_URL}`
  });

  // The split function takes three parameters:
  //
  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value
  const splitLink = split(
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query);
      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  const createApolloClient = () => {
    return new ApolloClient({
      cache: new InMemoryCache(),
      link: tokenLink.concat(splitLink)
    });
  };

  const client = createApolloClient();
  const classes = useStyles();
  return (
    <ApolloProvider client={client}>
      <StyledEngineProvider injectFirst>
        <SettingsProvider>
          <ThemeConfig>
            <ThemePrimaryColor>
              <GlobalStyles />
              <Toaster
                position="bottom-left"
                gutter={4}
                containerClassName={classes.containerToast}
              />
              <LocalizationProvider dateAdapter={DateAdapter}>
                {!isLoading ? <Router /> : <LoadingScreen />}
              </LocalizationProvider>
            </ThemePrimaryColor>
          </ThemeConfig>
        </SettingsProvider>
      </StyledEngineProvider>
    </ApolloProvider>
  );
};

export default App;
